import { LoadingButton } from '@mui/lab'
import {
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  Skeleton,
  Stack,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { ResponsiveDialog } from '@cibo/ui'
import { useUpdateAgreement } from './queries'
import { AgreementStatus } from './types'

const statusButtonColor: Record<AgreementStatus, ButtonProps['color']> = {
  agreed: 'success',
  concluded: 'success',
  pending: 'secondary',
  declined: 'error',
  expired: 'warning',
}
export const AgreementStatusUpdateModal = ({
  open,
  action,
  handleCloseModal,
  agreementId,
  agreementYear,
}: {
  open: boolean
  action?: AgreementStatus
  handleCloseModal(): void
  agreementId: string
  agreementYear: string
}) => {
  const { mutateAsync: updateAgreement } = useUpdateAgreement()
  const { t } = useTranslation('impact/pages/Agreements')

  return (
    <ResponsiveDialog open={open} onClose={handleCloseModal} fullWidth>
      <Formik
        initialValues={{
          note: '',
          status: action as AgreementStatus,
          agreementId,
        }}
        validationSchema={yup.object().shape({
          agreementId: yup.string().required(),
          status: yup
            .string()
            .required()
            .oneOf(['agreed', 'declined', 'pending']),
          note: yup.string().when('status', {
            is: val => val === 'declined' || val === 'pending',
            then: yup.string().required(),
          }),
        })}
        onSubmit={values => {
          updateAgreement(values).then(() => {
            handleCloseModal()
          })
        }}
        enableReinitialize
      >
        {({ resetForm, isValid, isSubmitting }) => (
          <Form>
            <DialogTitle>
              {!action ? (
                <Skeleton />
              ) : (
                t('dialogHeadline', { context: action, date: agreementYear })
              )}
            </DialogTitle>
            <DialogContent>
              <Stack>
                <InputLabel shrink htmlFor="note">
                  {!action ? (
                    <Skeleton />
                  ) : (
                    <>
                      {t('note')}
                      {(action === 'declined' || action === 'pending') && '*'}
                    </>
                  )}
                </InputLabel>
                {!action ? (
                  <Skeleton variant="rectangular" height="40px" width="100%" />
                ) : (
                  <>
                    <Field
                      id="note"
                      name="note"
                      component={TextField}
                      InputProps={{
                        inputProps: { multiline: true },
                      }}
                      multiline
                    />
                    {action === 'declined' && (
                      <FormHelperText>{t('noteHelp_declined')}</FormHelperText>
                    )}
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              {!action ? (
                <>
                  <Skeleton variant="rectangular" width="80px" height="36.5px" />
                  <Skeleton variant="rectangular" width="80px" height="36.5px" />
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      handleCloseModal()
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  {!!action && (
                    <LoadingButton
                      disabled={!isValid}
                      loading={isSubmitting}
                      type="submit"
                      variant="outlined"
                      color={statusButtonColor[action]}
                    >
                      {t(`${action}Button`)}
                    </LoadingButton>
                  )}
                </>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </ResponsiveDialog>
  )
}
